import { type DataNeeded, type ProCon } from "@portal/api/decisioning";
import { type FC, useState } from "react";
import { cn } from "../../../../lib/utils";
import { ActionMenu, Button, Card, ClickableText, Icon, Tag, Text } from "../../../../primitives";
import {
	CLOSE_REASON_OPTIONS,
	DATA_NEEDED_IMPORTANCE_LABELS,
	DATA_NEEDED_IMPORTANCE_TAG_COLORS,
	formatDecisioningDate,
} from "../../decisioningLabels";
import {
	JUMP_HIGHLIGHT_CLASSES,
	useDecisioningPanel,
	useJumpTargetProps,
} from "../DecisioningPanelContext";
import { ItemActionMenu } from "../ItemActionMenu";
import { DeleteRequestConfirmation } from "./DeleteRequestConfirmation";

export type RequestCardProps = {
	item: DataNeeded;
	/** The factor this request is linked to, when it is still active. */
	linkedProCon?: ProCon;
	/** The server has not confirmed this request yet; its actions stay disabled. */
	pending?: boolean;
	onReceive: () => void;
	onClose: (reason: (typeof CLOSE_REASON_OPTIONS)[number]["value"]) => void;
	onDelete: () => void;
};

/** One open request: collapsed it is a title and meta line, expanded it carries its actions. */
export const RequestCard: FC<RequestCardProps> = ({
	item,
	linkedProCon,
	pending = false,
	onReceive,
	onClose,
	onDelete,
}) => {
	const { isExpanded, setIsExpanded, jumpTo, openSheet } = useDecisioningPanel();
	const jumpProps = useJumpTargetProps(item.id);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const expanded = isExpanded(item.id);
	const { title } = item;
	const testId = `data-needed-card-${item.id}`;

	return (
		<Card
			density="compact"
			edge="info"
			className={cn("group/item scroll-mt-3", JUMP_HIGHLIGHT_CLASSES)}
			data-testid={testId}
			{...jumpProps}
		>
			<div className={cn("flex items-start gap-2 pl-4 pr-3 pt-2.5", expanded ? "pb-2" : "pb-2.5")}>
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<button
						type="button"
						aria-expanded={expanded}
						onClick={() => setIsExpanded(item.id, !expanded)}
						className="block w-full cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-bc-focus"
						data-testid={`${testId}-toggle`}
					>
						<Text
							type="body"
							color="primary"
							className={cn(
								"block font-medium [text-box-trim:none]",
								expanded && "whitespace-pre-wrap break-words",
							)}
							truncate={!expanded}
						>
							{title}
						</Text>
					</button>
					<div className="flex flex-wrap items-center gap-x-2.5">
						<Text type="caption" color="tertiary" className="[text-box-trim:none]">
							{item.type}
						</Text>
						<Text type="caption" color="tertiary" className="[text-box-trim:none]">
							{formatDecisioningDate(item.createdAt)}
						</Text>
						{item.assignee !== "Agent" && (
							<Text
								type="caption"
								color="tertiary"
								className="[text-box-trim:none]"
								data-testid={`${testId}-owner`}
							>
								{item.assignee}
							</Text>
						)}
					</div>
					{linkedProCon && (
						<div className="flex min-w-0 items-center gap-1">
							<Icon type="scale" size="micro" color="tertiary" aria-hidden />
							<ClickableText
								type="caption"
								color="link"
								className="min-w-0 truncate"
								testId={`${testId}-factor-link`}
								onClick={() => jumpTo({ tab: "factors", id: linkedProCon.id })}
							>
								{linkedProCon.factor.name}
							</ClickableText>
						</div>
					)}
				</div>
				<Tag
					color={DATA_NEEDED_IMPORTANCE_TAG_COLORS[item.importance]}
					testId={`${testId}-importance`}
				>
					{DATA_NEEDED_IMPORTANCE_LABELS[item.importance]}
				</Tag>
				<ItemActionMenu itemName={title} testId={`${testId}-menu`}>
					<ActionMenu.Item
						disabled={pending}
						onClick={() => openSheet({ kind: "request", mode: "edit", dataNeededId: item.id })}
						data-testid={`${testId}-edit`}
					>
						Edit
					</ActionMenu.Item>
					<ActionMenu.Item
						tone="danger"
						disabled={pending}
						onClick={() => setConfirmingDelete(true)}
						data-testid={`${testId}-delete`}
					>
						Delete
					</ActionMenu.Item>
				</ItemActionMenu>
			</div>

			{expanded && (
				<div className="flex flex-col gap-3 pb-3 pl-4 pr-3" data-testid={`${testId}-detail`}>
					<div className="flex flex-col gap-1.5">
						{item.description !== null && item.description.trim() !== "" && (
							<Text
								type="caption"
								color="secondary"
								className="block whitespace-pre-wrap break-words [text-box-trim:none]"
								data-testid={`${testId}-description`}
							>
								<span className="font-semibold">Description: </span>
								{item.description}
							</Text>
						)}
						<Text
							type="caption"
							color="secondary"
							className="block whitespace-pre-wrap break-words [text-box-trim:none]"
						>
							<span className="font-semibold">Why: </span>
							{item.rationale}
						</Text>
					</div>
					<div className="flex flex-wrap items-center gap-1.5">
						<Button
							color="confirm"
							variant="outline"
							size="sm"
							disabled={pending}
							onClick={onReceive}
							data-testid={`${testId}-receive`}
						>
							Mark received
						</Button>
						<ActionMenu
							trigger={
								<span className="inline-flex items-center gap-1">
									Close
									<Icon type="chevron-down" size="micro" aria-hidden />
								</span>
							}
							triggerAriaLabel="Close with a reason"
							triggerClassName="h-7 rounded-[4px] border border-button-secondary px-3 text-xs font-semibold text-secondary disabled:cursor-not-allowed disabled:opacity-[0.45]"
							anchor="bottom start"
							testId={`${testId}-close-menu`}
						>
							<ActionMenu.Label>Reason for closing</ActionMenu.Label>
							{CLOSE_REASON_OPTIONS.map((option) => (
								<ActionMenu.Item
									key={option.value}
									disabled={pending}
									onClick={() => onClose(option.value)}
									data-testid={`${testId}-close-${option.value}`}
								>
									{option.label}
								</ActionMenu.Item>
							))}
						</ActionMenu>
					</div>
				</div>
			)}

			{confirmingDelete && (
				<DeleteRequestConfirmation
					title={title}
					testId={testId}
					onCancel={() => setConfirmingDelete(false)}
					onConfirm={() => {
						setConfirmingDelete(false);
						onDelete();
					}}
				/>
			)}
		</Card>
	);
};
